import React from "react";
import { DataTable } from "@/components/DataTable";
import { EmptyState } from "@/components/DataTable/States/Empty";
import { CalendarClock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useFeedPostsColumns } from "./columns";
import { TableHeader } from "./TableHeader";
import { useFeedPostTab } from "./useFeedPostTab";
import {
  FeedPostOrderingInput,
  FeedPostStatusEnum,
} from "@/__generated__/graphql";
import { i18n } from "@/i18n";

interface FeedPostTabProps {
  statuses: FeedPostStatusEnum[];
  title: string;
  subtitle: string;
  emptyState: string;
  ordering: FeedPostOrderingInput[];
}

export const FeedPostTab: React.FC<FeedPostTabProps> = ({
  statuses,
  title,
  subtitle,
  emptyState,
  ordering,
}) => {
  const { t } = useTranslation();
  const columns = useFeedPostsColumns({
    publishDateHeader: getPublishedDateHeader(statuses),
  });
  const {
    feedPostsData,
    loading,
    hasNextPage,
    hasPreviousPage,
    pageIndex,
    pageCount,
    feedPosters,
    handleFilterApply,
    getFilterDisplayValue,
    handleNextPage,
    handlePreviousPage,
  } = useFeedPostTab(statuses, ordering);

  // const handleCreateNewPost = () => {
  // No route exists for creating feed posts yet, so do nothing
  // TODO: Update this when create feed post routes are added
  // };

  return (
    <DataTable
      data={feedPostsData}
      columns={columns}
      isLoading={loading}
      pagination={
        <>
          <DataTable.PaginationLabel
            pageCount={pageCount}
            pageIndex={pageIndex}
          />
          <DataTable.PaginationControls
            onNextPage={hasNextPage ? handleNextPage : false}
            onPreviousPage={hasPreviousPage ? handlePreviousPage : false}
          />
        </>
      }
      tableHeader={<DataTable.THeader />}
      tableBody={<DataTable.TBody />}
      emptyState={
        <EmptyState
          copy={emptyState}
          // buttonCopy={t("feedPost.table.createNewPost")}
          // buttonTarget={handleCreateNewPost}
          icon={
            <CalendarClock
              className="h-[36px] w-[36px] text-primary-light"
              aria-label={t("feedPost.table.createNewPost")}
            />
          }
        />
      }
      topHeader={
        <TableHeader
          title={title}
          subtitle={subtitle}
          onFilterChange={handleFilterApply}
          filterDisplayValue={getFilterDisplayValue()}
          feedPosters={feedPosters}
        />
      }
    />
  );
};

function getPublishedDateHeader(statuses: FeedPostStatusEnum[]) {
  if (statuses.length !== 1) {
    return i18n.t("feedPost.table.columns.postedAt");
  }

  const [status] = statuses;
  switch (status) {
    case FeedPostStatusEnum.Scheduled:
      return i18n.t("feedPost.table.scheduled.columns.scheduledFor");
    default:
      return i18n.t("feedPost.table.columns.postedAt");
  }
}
